/**
 * 🛡️ Organization Access Control Service
 * 
 * Handles organization-bound access control ensuring users cannot access
 * the platform when their organization is deleted, inactive, or when they
 * have invalid CMT assignments.
 */

import { 
  doc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * 🔍 Validate user's organization access
 */
export async function validateOrganizationAccess(userId, orgId) {
  try {
    // Check if organization exists and is active
    const orgDoc = await getDoc(doc(db, 'c3_organisations', orgId));
    
    if (!orgDoc.exists()) {
      return {
        hasAccess: false,
        reason: 'ORGANIZATION_NOT_FOUND',
        message: 'Your organization no longer exists. Please contact support.',
        shouldLogout: true
      };
    }

    const orgData = orgDoc.data();

    // Check if organization is active
    if (orgData.status === 'deleted') {
      return {
        hasAccess: false,
        reason: 'ORGANIZATION_DELETED',
        message: 'Your organization has been deleted. You no longer have access to this platform.',
        shouldLogout: true
      };
    }

    if (orgData.status === 'inactive' || orgData.isActive === false) {
      return {
        hasAccess: false,
        reason: 'ORGANIZATION_INACTIVE',
        message: 'Your organization has been deactivated. Please contact your administrator.',
        shouldLogout: true
      };
    }

    // Check if organization is suspended
    if (orgData.status === 'suspended') {
      return {
        hasAccess: false,
        reason: 'ORGANIZATION_SUSPENDED',
        message: 'Your organization has been suspended. Please contact support.',
        shouldLogout: true
      };
    }

    return {
      hasAccess: true,
      organization: orgData
    };

  } catch (error) {
    console.error('Error validating organization access:', error);
    
    // If it's a permissions error, the organization likely doesn't exist
    if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
      return {
        hasAccess: false,
        reason: 'ORGANIZATION_DELETED',
        message: 'Your organisation no longer has access to this platform. Please contact your CMT Admin for assistance.',
        shouldLogout: true
      };
    }
    
    // For other errors, return generic validation error
    return {
      hasAccess: false,
      reason: 'VALIDATION_ERROR',
      message: 'Unable to verify your organization access. Please try again later.',
      shouldLogout: false
    };
  }
}

/**
 * 🔍 Validate user's account status
 */
export async function validateUserAccess(userId) {
  try {
    const userDoc = await getDoc(doc(db, 'c3_users', userId));
    
    if (!userDoc.exists()) {
      return {
        hasAccess: false,
        reason: 'USER_NOT_FOUND',
        message: 'Your user account was not found. Please contact support.',
        shouldLogout: true
      };
    }

    const userData = userDoc.data();

    // Check user status
    if (userData.status === 'deactivated') {
      return {
        hasAccess: false,
        reason: 'USER_DEACTIVATED',
        message: 'Your account has been deactivated. Please contact your administrator.',
        shouldLogout: true
      };
    }

    if (userData.status === 'suspended') {
      return {
        hasAccess: false,
        reason: 'USER_SUSPENDED',
        message: 'Your account has been suspended. Please contact support.',
        shouldLogout: true
      };
    }

    if (userData.status === 'invited') {
      return {
        hasAccess: true,
        reason: 'USER_INVITED',
        message: 'Please complete your account setup by setting your password.',
        requiresSetup: true,
        user: userData
      };
    }

    return {
      hasAccess: true,
      user: userData
    };

  } catch (error) {
    console.error('Error validating user access:', error);
    return {
      hasAccess: false,
      reason: 'USER_VALIDATION_ERROR',
      message: 'Unable to verify your account status. Please try again later.',
      shouldLogout: false
    };
  }
}

/**
 * 🔍 Validate user's CMT assignments
 */
export async function validateCMTAccess(userId, orgId) {
  try {
    const userDoc = await getDoc(doc(db, 'c3_users', userId));
    if (!userDoc.exists()) {
      return {
        hasAccess: false,
        reason: 'USER_NOT_FOUND'
      };
    }

    const userData = userDoc.data();
    const cmtAssignments = userData.cmtAssignments || [];
    
    if (cmtAssignments.length === 0) {
      // No CMT assignments - still allow access but show banner
      return {
        hasAccess: true,
        hasCMTIssues: true,
        reason: 'NO_CMT_ASSIGNMENTS',
        message: 'You are not assigned to any Crisis Management Teams. Please contact your administrator.',
        showBanner: true
      };
    }

    // Check if assigned CMTs still exist
    const activeCMTs = [];
    const missingCMTs = [];
    
    for (const assignment of cmtAssignments) {
      if (!assignment.isActive) continue;
      
      try {
        const cmtDoc = await getDoc(doc(db, 'c3_organisations', orgId, 'cmts', assignment.cmtId));
        if (cmtDoc.exists()) {
          activeCMTs.push({
            ...assignment,
            cmtData: cmtDoc.data()
          });
        } else {
          missingCMTs.push(assignment);
        }
      } catch (error) {
        console.warn(`Error checking CMT ${assignment.cmtId}:`, error);
        missingCMTs.push(assignment);
      }
    }

    if (activeCMTs.length === 0 && missingCMTs.length > 0) {
      return {
        hasAccess: true,
        hasCMTIssues: true,
        reason: 'ALL_CMTS_MISSING',
        message: 'Your assigned teams no longer exist. Please contact your administrator.',
        showBanner: true,
        missingCMTs: missingCMTs
      };
    }

    if (missingCMTs.length > 0) {
      return {
        hasAccess: true,
        hasCMTIssues: true,
        reason: 'SOME_CMTS_MISSING',
        message: `Some of your assigned teams no longer exist. Please contact your administrator.`,
        showBanner: true,
        activeCMTs: activeCMTs,
        missingCMTs: missingCMTs
      };
    }

    return {
      hasAccess: true,
      activeCMTs: activeCMTs
    };

  } catch (error) {
    console.error('Error validating CMT access:', error);
    return {
      hasAccess: true,
      hasCMTIssues: true,
      reason: 'CMT_VALIDATION_ERROR',
      message: 'Unable to verify your team assignments. Some features may not work properly.',
      showBanner: true
    };
  }
}

/**
 * 🔍 Complete access validation (organization + user + CMT)
 */
export async function validateCompleteAccess(userId, orgId) {
  try {
    // 🚨 SUPER ADMIN BYPASS - Skip all validation for Super Admin
    // Note: Super admin status checked via superAdmin field in user document
    const userDoc = await getDoc(doc(db, 'c3_users', userId));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    if (userData?.superAdmin === true) {
      console.log('🔓 Super Admin detected - bypassing all access validation');
      return {
        hasAccess: true,
        reason: 'SUPER_ADMIN_ACCESS',
        user: { uid: userId, role: 'super_admin' },
        organization: { id: orgId, name: 'Crisis Compass Platform', status: 'active' }
      };
    }

    // 1. Validate organization access
    const orgValidation = await validateOrganizationAccess(userId, orgId);
    if (!orgValidation.hasAccess) {
      return orgValidation;
    }

    // 2. Validate user access
    const userValidation = await validateUserAccess(userId);
    if (!userValidation.hasAccess) {
      return userValidation;
    }

    // 3. Validate CMT access (non-blocking)
    const cmtValidation = await validateCMTAccess(userId, orgId);

    return {
      hasAccess: true,
      organization: orgValidation.organization,
      user: userValidation.user,
      cmtIssues: cmtValidation.hasCMTIssues ? cmtValidation : null,
      requiresSetup: userValidation.requiresSetup
    };

  } catch (error) {
    console.error('Error in complete access validation:', error);
    return {
      hasAccess: false,
      reason: 'COMPLETE_VALIDATION_ERROR',
      message: 'Unable to verify your access permissions. Please try again later.',
      shouldLogout: false
    };
  }
}

/**
 * 🚪 Force user logout with reason
 */
export async function forceLogout(reason, userId = null, orgId = null) {
  try {
    // Log the logout reason for audit
    await logAccessDenial(reason, userId, orgId);
    
    // Sign out from Firebase Auth
    await signOut(auth);
    
    console.log(`🚪 User logged out due to: ${reason}`);
    
  } catch (error) {
    console.error('Error during forced logout:', error);
    // Even if logging fails, still try to sign out
    try {
      await signOut(auth);
    } catch (signOutError) {
      console.error('Error signing out:', signOutError);
    }
  }
}

/**
 * 📝 Log access denial for audit trail
 */
export async function logAccessDenial(reason, userId, orgId, additionalData = {}) {
  try {
    const logEntry = {
      action: 'ACCESS_DENIED',
      reason: reason,
      userId: userId,
      orgId: orgId,
      timestamp: serverTimestamp(),
      category: 'SECURITY',
      severity: 'WARNING',
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...additionalData
      }
    };

    await addDoc(collection(db, 'platform_audit_logs'), logEntry);
    
  } catch (error) {
    console.error('Error logging access denial:', error);
    // Don't fail the main process for logging issues
  }
}

/**
 * 🔄 Check organization reactivation status
 */
export async function checkOrganizationReactivation(orgId) {
  try {
    const orgDoc = await getDoc(doc(db, 'c3_organisations', orgId));
    
    if (!orgDoc.exists()) {
      return { canReactivate: false, reason: 'Organization not found' };
    }

    const orgData = orgDoc.data();
    
    // Can reactivate if currently inactive but not deleted
    if (orgData.status === 'inactive' && orgData.status !== 'deleted') {
      return {
        canReactivate: true,
        organization: orgData,
        preserveUserData: true
      };
    }

    return {
      canReactivate: false,
      reason: `Organization status: ${orgData.status}`,
      organization: orgData
    };

  } catch (error) {
    console.error('Error checking reactivation status:', error);
    return {
      canReactivate: false,
      reason: 'Error checking status',
      error: error.message
    };
  }
}

/**
 * 📊 Get access validation summary for admin dashboard
 */
export async function getAccessValidationSummary(orgId) {
  try {
    // Get all users in organization
    const usersRef = collection(db, 'c3_users');
    const q = query(usersRef, where('orgId', '==', orgId));
    const snapshot = await getDocs(q);
    
    const summary = {
      totalUsers: 0,
      activeUsers: 0,
      invitedUsers: 0,
      deactivatedUsers: 0,
      usersWithCMTIssues: 0,
      organizationStatus: 'unknown'
    };

    // Check organization status
    const orgDoc = await getDoc(doc(db, 'c3_organisations', orgId));
    if (orgDoc.exists()) {
      summary.organizationStatus = orgDoc.data().status || 'active';
    }

    // Analyze user statuses
    for (const userDoc of snapshot.docs) {
      const userData = userDoc.data();
      summary.totalUsers++;
      
      switch (userData.status) {
        case 'active':
          summary.activeUsers++;
          break;
        case 'invited':
          summary.invitedUsers++;
          break;
        case 'deactivated':
          summary.deactivatedUsers++;
          break;
        default:
          // Handle unknown status
          break;
      }
      
      // Check for CMT issues
      const cmtValidation = await validateCMTAccess(userDoc.id, orgId);
      if (cmtValidation.hasCMTIssues) {
        summary.usersWithCMTIssues++;
      }
    }

    return summary;

  } catch (error) {
    console.error('Error getting access validation summary:', error);
    return {
      error: error.message,
      totalUsers: 0,
      activeUsers: 0,
      invitedUsers: 0,
      deactivatedUsers: 0,
      usersWithCMTIssues: 0,
      organizationStatus: 'error'
    };
  }
}

const orgAccessControlService = {
  validateOrganizationAccess,
  validateUserAccess,
  validateCMTAccess,
  validateCompleteAccess,
  forceLogout,
  logAccessDenial,
  checkOrganizationReactivation,
  getAccessValidationSummary
};

export default orgAccessControlService;
